/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-external-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import StorageKey from '@linc-technologies/linced-common/enums/storage-key';
import moment from 'moment';
import config from 'linced-app/config/environment';
import { byKeys } from '@linc-technologies/linced-common/utils/arrays';

export default class FlameUpdates extends Component {
  @service
  session;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @alias('session.school.name')
  schoolName;

  tagMap = Object.freeze(
    new Map([
      [0, []],
      [1, ['NZ']],
      [3, ['AUS', 'ACT']],
      [4, ['AUS', 'NSW']],
      [5, ['AUS', 'NT']],
      [6, ['AUS', 'QLD']],
      [7, ['AUS', 'SA']],
      [10, ['AUS', 'TAS']],
      [8, ['AUS', 'VIC']],
      [9, ['AUS', 'WA']],
      [2, ['Independent']],
      [11, ['ASIA', 'HK']],
      [12, ['ASIA', 'MY']],
      [14, ['UK']],
      [13, ['USA']],
    ]),
  );

  // `tags` are the set of HubSpot categories this school is allowed to see. A flame post
  // is shown only if one of its categories matches a tag here (untagged posts
  // are shown to everyone). Tags come from the school's jurisdiction, plus
  // 'Port Vila' - which shares jurisdiction 2 with all
  // other Independent schools, so they're singled out by name instead.
  @computed('jurisdiction', 'schoolName')
  get tags() {
    let tags = (this.tagMap.get(this.jurisdiction) || []).slice();
    if ((this.schoolName || '').toLowerCase().includes('port vila')) {
      tags.push('Port Vila');
    }
    return tags;
  }

  dialogOpen = false;
  @tracked
  notification = false;
  link = 'https://our-hero.com/hero-flame-updates';
  @tracked
  feed = null;
  @tracked
  blogPost = null;

  getIntersection(list1, list2) {
    // Create a Set of IDs from list2 for faster lookup
    let idSet = new Set(list2.map((item) => item.guid));

    // Filter list1 to only include items whose IDs are present in list2
    return list1.filter((item) => idSet.has(item.guid));
  }

  getNewItems(list1, list2) {
    // Create a Set of IDs from list2 for faster lookup
    let idSet = new Set(list2.map((item) => item.guid));

    // Filter list1 to only include items whose IDs are not present in list2
    return list1.filter((item) => !idSet.has(item.guid));
  }

  init() {
    super.init(...arguments);

    let existingFeed =
      JSON.parse(localStorage.getItem(StorageKey.FLAME_FEED)) || [];

    // Using direct fetch as hubspot articles are publicly available.
    fetch(config.APP.endpoints.flame)
      .then((response) => response.json())
      .then((rssFeed) => {
        // get truncated RSS feed
        let numPosts = 4;
        rssFeed = rssFeed
          .filter(
            (obj) =>
              isEmpty(obj.category) ||
              this.tags.includes(obj.category) ||
              (Array.isArray(obj.category) &&
                this.tags.some((tag) => obj.category.includes(tag))),
          )
          .slice(0, numPosts);

        let newFeed = [];
        let lastPostMoment = 0;
        if (existingFeed.length) {
          lastPostMoment = existingFeed[0].pubTime;
          // Get intersect of rssFeed & existingFeed & handle updates/deletions
          let intersection = this.getIntersection(rssFeed, existingFeed);
          intersection.forEach((item) => {
            existingFeed.forEach((existItem) => {
              if (item.guid === existItem.guid) {
                item.pubTime = existItem.pubTime;
                item.unread = existItem.unread;
                item.body = item.description;
                if (item.category) {
                  item.tags = Array.isArray(item.category)
                    ? item.category
                    : [item.category];
                }
              }
            });
            newFeed.push(item);
          });
        }
        let newItems = this.getNewItems(rssFeed, existingFeed);
        newItems.forEach((feedItem) => {
          let post = {
            title: feedItem.title,
            body: feedItem.description,
            pubTime: feedItem['dc:date'],
            pubDate: feedItem.pubDate,
            unread: moment(feedItem['dc:date']).isAfter(moment(lastPostMoment)),
            guid: feedItem.guid,
          };
          if (feedItem.category) {
            post.tags = Array.isArray(feedItem.category)
              ? feedItem.category
              : [feedItem.category];
          }
          newFeed.push(post);
        });
        if (newFeed.some((post) => post['unread'])) {
          this.notification = true;
        }
        this.feed = newFeed.sort(byKeys('pubTime')).reverse();
        localStorage.setItem(StorageKey.FLAME_FEED, JSON.stringify(this.feed));
      })
      .catch(() => {});
  }

  @action
  toggleDialog() {
    this.toggleProperty('dialogOpen');
    if (!this.dialogOpen) {
      this.blogPost = null;
      this.notification = false;
    }
  }

  @action
  enterBlogPost(post) {
    this.blogPost = post;
    set(post, 'unread', false);
    localStorage.setItem(StorageKey.FLAME_FEED, JSON.stringify(this.feed));
  }

  @action
  exitBlogPost() {
    this.blogPost = null;
  }
}
